import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { filterValues } from 'Helper'

const DEVICE_MODELS = ['PAX A920 Pro', 'PAX A920', 'PAX A80', 'Verifone P400', 'Ingenico Move 5000']
const DEVICE_TYPES = ['Android POS', 'Linux POS', 'Card Reader', 'Mobile POS']

export default withTranslation()(function AddPOS({ t, device, handleClick, onSaved }) {
  const dispatch = useDispatch()
  const [step, setStep] = useState(1)
  const [photoPreview, setPhotoPreview] = useState(null)
  const { data: branchesRes } = useSWR('merchants/branches')
  const { data: usersRes } = useSWR('pos/available-users')
  const branches = branchesRes?.data ?? []
  const users = usersRes?.data ?? []

  const method = useForm({ mode: 'all' })
  const { watch, reset, setValue, handleSubmit } = method

  useEffect(() => {
    if (device) {
      reset({
        serial_number: device.serial_number,
        device_model: device.device_model,
        device_type: device.device_type,
        software_version: device.software_version,
        activation_date: device.activation_date,
        branch_id: device.branch_id,
        user_id: device.user_id
      })
    }
  }, [device, reset])

  const [serial_number, device_model, device_type] = watch(['serial_number', 'device_model', 'device_type'])
  const [branch_id, user_id] = watch(['branch_id', 'user_id'])

  const step1Valid = Boolean(serial_number && device_model && device_type)
  const step2Valid = Boolean(branch_id && user_id)

  const handlePhoto = (fileList) => {
    const file = fileList?.[0]
    if (!file) return
    setPhotoPreview(URL.createObjectURL(file))
    setValue('photo', file)
  }

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      await apiRequest({
        method: device ? 'put' : 'post',
        url: device ? `pos/${device.id}` : 'pos',
        payload: { ...payload, name: payload.serial_number }
      })

      toast.success(t(device ? 'pos.management.success_edit' : 'pos.management.success_create'))
      onSaved && onSaved()
      handleClick()
    } catch ({ message }) {
      toast.error(message)
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative w-11/12 max-w-md rounded-xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8 pb-0 shrink-0">
          <div className="relative flex items-center justify-center mb-4">
            <Btn
              className="w-10 h-10 hover:bg-default absolute left-0"
              content={() => <UIcons.UilTimes className="text-secondary" />}
              click={handleClick}
            />

            <h3 className="text-xl font-bold text-dark-blue">{t(device ? 'pos.management.edit' : 'pos.management.add')}</h3>
          </div>

          <div className="w-full h-1.5 bg-border-lines rounded-full mb-6 flex gap-1 overflow-hidden">
            <div className="h-full bg-primary rounded-full flex-1" />
            <div className={`h-full rounded-full flex-1 ${step === 2 ? 'bg-primary' : 'bg-border-lines'}`} />
          </div>
        </div>

        <div className="overflow-y-auto px-6 md:px-8 pb-6 md:pb-8">
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(submit)}>
            {
              step === 1 ? (
                <div key="step-1">
                  <div className="mb-3">
                    <Input
                      name="serial_number"
                      label={t('pos.management.form.serial_number.label')}
                      placeholder={t('pos.management.form.serial_number.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="device_model"
                      label={t('pos.management.form.device_model.label')}
                      placeholder={t('pos.management.form.device_model.placeholder')}
                      variant="outlined"
                      options={DEVICE_MODELS.map((model) => ({ label: model, value: model }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="device_type"
                      label={t('pos.management.form.device_type.label')}
                      placeholder={t('pos.management.form.device_type.placeholder')}
                      variant="outlined"
                      options={DEVICE_TYPES.map((type) => ({ label: type, value: type }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="software_version"
                      label={t('pos.management.form.software_version.label')}
                      placeholder={t('pos.management.form.software_version.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="activation_date"
                      type="date"
                      label={t('pos.management.form.activation_date.label')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="text-secondary text-sm inline-block mb-2">{t('pos.management.form.photo.label')}</label>

                    <div
                      className="p-4 w-full text-center rounded-xl border-2 border-dashed border-border-lines flex flex-col justify-center items-center cursor-pointer"
                      onClick={() => document.getElementById('pos-photo-upload')?.click()}
                    >
                      <UIcons.UilUpload className="text-primary mb-1" size={18} />
                      <span className="font-medium text-primary text-sm">{t('pos.management.form.photo.upload')}</span>
                      <span className="text-xs text-placeholder">{t('pos.management.form.photo.hint')}</span>
                      {photoPreview && <img src={photoPreview} alt="" className="w-16 h-16 object-cover rounded-md mt-2" />}
                    </div>

                    <input
                      type="file"
                      id="pos-photo-upload"
                      className="hidden"
                      accept="application/pdf,image/jpeg,image/png"
                      onChange={(e) => handlePhoto(e.target.files)}
                    />
                  </div>

                  <div className="flex justify-end mt-5">
                    <Btn
                      content={t('misc.next')}
                      className="h-12 px-6 rounded-xl bg-primary text-white disabled:opacity-40"
                      click={() => setStep(2)}
                      disabled={!step1Valid}
                    />
                  </div>
                </div>
              ) : (
                <div key="step-2">
                  <div className="mb-3">
                    <Input
                      name="branch_id"
                      label={t('pos.management.form.branch_id.label')}
                      placeholder={t('pos.management.form.branch_id.placeholder')}
                      variant="outlined"
                      options={filterValues({ options: branches, param: { value: 'id', label: 'name' } })}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="user_id"
                      label={t('pos.management.form.user_id.label')}
                      placeholder={t('pos.management.form.user_id.placeholder')}
                      variant="outlined"
                      options={filterValues({ options: users, param: { value: 'id', label: 'name' } })}
                    />
                  </div>

                  <div className="flex justify-between mt-5">
                    <Btn
                      content={() => <><UIcons.UilArrowLeft size={16} className="mr-2" /> {t('misc.back')}</>}
                      className="h-12 px-6 rounded-xl border border-primary text-primary"
                      click={() => setStep(1)}
                      variant="outline"
                      color="#1976D2"
                    />

                    <FormBtn
                      content={t('misc.save')}
                      className="h-12 px-6 rounded-xl bg-primary text-white disabled:opacity-40"
                      disabled={!step2Valid}
                    />
                  </div>
                </div>
              )
            }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
})
